import { faSpinnerScale } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/sharp-regular-svg-icons";
import { Menu } from "@headlessui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import {
    type EmissionRecordView,
    EventStatusEnum,
} from "../../../../apiClient/generated";
import { useUser } from "../../../../auth";
import {
    useEmissionEventsApiClient,
    useEmissionRecordsApiClient,
} from "../../../../hooks";
import { ConfirmationDialog } from "../../../../ui/ConfirmationDialog";
import {
    EMISSIONS_TABLE_PREFIX,
    invalidateKeyPrefix,
    invalidateSiteEmissions,
} from "../../../../utils/queryInvalidation";
import { useCompanyDataForEmission } from "../../../EventsV2/helpers";

/**
 * Extracts logic to move emission back to "In-progress"
 *
 * @param emission
 * @returns function
 */
const useEmissionBackToStart = (emission: EmissionRecordView) => {
    const queryClient = useQueryClient();
    const eventsApiClient = useEmissionEventsApiClient();

    return async () => {
        if (
            emission.mainEvent.eventStatus === "REJECTED" ||
            emission.mainEvent.eventStatus === "COMPLETED" ||
            emission.mainEvent.eventStatus === "ARCHIVED"
        ) {
            await eventsApiClient.emissioneventsPartialUpdate({
                id: emission.mainEvent.id,
                patchedEmissionEventPartialUpdateRequest: {
                    eventStatus: "WORK_IN_PROGRESS",
                },
            });
            invalidateKeyPrefix(queryClient, EMISSIONS_TABLE_PREFIX);
            invalidateSiteEmissions(queryClient);
            queryClient.invalidateQueries({
                queryKey: ["emissionRecord", emission.id],
            });
            queryClient.invalidateQueries({
                queryKey: ["emissionsRelatedToEvent"],
            });
            queryClient.invalidateQueries({
                queryKey: ["event", emission.mainEvent.id],
            });
        } else {
            throw new Error(
                "Emission is not in a state that can be moved back to in progress: " +
                    emission.id,
            );
        }
    };
};

const useArchiveEmission = (emission: EmissionRecordView) => {
    const queryClient = useQueryClient();
    const eventsApiClient = useEmissionEventsApiClient();

    return async () => {
        await eventsApiClient.emissioneventsPartialUpdate({
            id: emission.mainEvent.id,
            patchedEmissionEventPartialUpdateRequest: {
                eventStatus: "ARCHIVED",
            },
        });
        invalidateKeyPrefix(queryClient, EMISSIONS_TABLE_PREFIX);
        invalidateSiteEmissions(queryClient);
        queryClient.invalidateQueries({
            queryKey: ["emissionRecord", emission.id],
        });
        queryClient.invalidateQueries({
            queryKey: ["emissionsRelatedToEvent"],
        });
        queryClient.invalidateQueries({
            queryKey: ["event", emission.mainEvent.id],
        });
    };
};

interface ClosedEventFormActionsProps {
    emission: EmissionRecordView;
    onClose: () => void;
}

export const ClosedEventFormActions = ({
    emission,
    onClose,
}: ClosedEventFormActionsProps) => {
    const moveEmissionBackToStart = useEmissionBackToStart(emission);
    return (
        <button
            type="button"
            onClick={async () => {
                await moveEmissionBackToStart();
                onClose();
            }}
            className="border px-4 py-1.5 rounded border-opacity-20 border-white text-white"
        >
            Move back to "In Progress"
        </button>
    );
};

interface EventFormActionsProps {
    emission: EmissionRecordView;
    onClose: () => void;
    // Form control arguments
    form?: UseFormReturn<any>;
    submit?: any;
}

export const EventFormActions = ({
    emission,
    onClose,
    form,
    submit,
}: EventFormActionsProps) => {
    const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
    const [showLinkConfirm, setShowLinkConfirm] = useState(false);
    const [pendingLinkEvent, setPendingLinkEvent] = useState<React.MouseEvent<
        HTMLButtonElement,
        MouseEvent
    > | null>(null);
    const archiveEmission = useArchiveEmission(emission);
    const emissionsApiClient = useEmissionRecordsApiClient();

    const allowLinking =
        emission.mainEvent.eventStatus === "NEW" ||
        emission.mainEvent.eventStatus === "CREATED" ||
        emission.mainEvent.eventStatus === "WORK_IN_PROGRESS";

    const similarEmissionsQuery = useQuery({
        queryKey: ["similarEmissions", emission.id],
        queryFn: async () =>
            await emissionsApiClient.emissionRecordsGetNearbyEventsList({
                id: emission.id,
            }),
        refetchOnWindowFocus: false,
        enabled: allowLinking,
    });

    // Get company settings based on user type.
    const { isLoading, company } = useCompanyDataForEmission({
        emissionRecord: emission,
    });
    const { permissions: userPermissions, isStaff } = useUser();

    // Show loading spinner while fetching company data for superuser
    if (isLoading) {
        return (
            <div className="flex gap-2 items-center">
                <FontAwesomeIcon
                    icon={faSpinnerScale}
                    className="animate-spin"
                />
                <span className="text-sm text-gray-600">
                    Loading company settings...
                </span>
            </div>
        );
    }

    // Check if user has required permissions
    const hasEditPermissions =
        userPermissions.emissionEdit && userPermissions.emissionEventEdit;

    // Don't show any actions if user doesn't have permissions
    if (!hasEditPermissions) {
        return (
            <div className="flex gap-2 items-center">
                <span className="text-sm text-white">
                    You don't have permission to edit this emission.
                </span>
            </div>
        );
    }

    // Normal component logic from here
    const site = form.watch("site");
    const similarEmission = form.watch("similarEmission");
    const actions: {
        label: string;
        action: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
        disabled: boolean;
    }[] = [];

    const targetEmission = similarEmissionsQuery.data?.find(
        (em) => em.mainEvent?.id === similarEmission,
    );
    const targetIsCompleted =
        company?.settings?.approvalFlowEnabled &&
        targetEmission?.mainEvent?.eventStatus === "COMPLETED";
    const currentIsWip = emission.mainEvent.eventStatus === "WORK_IN_PROGRESS";

    if (similarEmission) {
        actions.push({
            label: "Link Emission",
            action: async (e) => {
                if (currentIsWip || targetIsCompleted) {
                    setPendingLinkEvent(e);
                    setShowLinkConfirm(true);
                    return;
                }
                form.setValue("eventStatus", EventStatusEnum.Linked);
                await submit(e);
                onClose();
            },
            disabled: form.formState.isSubmitting,
        });
    } else {
        if (company.settings?.approvalFlowEnabled) {
            if (
                emission.mainEvent.eventStatus === "NEW" ||
                emission.mainEvent.eventStatus === "CREATED"
            ) {
                actions.push({
                    label: "Accept and start investigation",
                    action: (e) => {
                        form.setValue(
                            "eventStatus",
                            EventStatusEnum.WorkInProgress,
                        );
                        submit(e);
                    },
                    disabled: form.formState.isSubmitting,
                });
                actions.push({
                    label: "Send to coordinator for approval",
                    action: (e) => {
                        form.setValue(
                            "eventStatus",
                            EventStatusEnum.WaitingApproval,
                        );
                        submit(e);
                    },
                    disabled: form.formState.isSubmitting,
                });
                if (userPermissions.emissionEventArchive) {
                    actions.push({
                        label: "Archive (no investigation)",
                        action: () => {
                            setShowArchiveConfirm(true);
                        },
                        disabled: form.formState.isSubmitting,
                    });
                }
            } else if (emission.mainEvent.eventStatus === "WORK_IN_PROGRESS") {
                actions.push({
                    label: "Send for coordinator approval",
                    action: (e) => {
                        form.setValue(
                            "eventStatus",
                            EventStatusEnum.WaitingApproval,
                        );
                        submit(e);
                    },
                    disabled: form.formState.isSubmitting,
                });
                actions.push({
                    label: "Save and continue later",
                    action: () => {
                        form.setValue(
                            "eventStatus",
                            emission.mainEvent.eventStatus,
                        );
                        submit();
                    },
                    disabled: form.formState.isSubmitting,
                });
                if (userPermissions.emissionEventArchive) {
                    actions.push({
                        label: "Archive",
                        action: () => {
                            form.setValue(
                                "eventStatus",
                                EventStatusEnum.Archived,
                            );
                            submit();
                        },
                        disabled: form.formState.isSubmitting,
                    });
                }
            } else if (emission.mainEvent.eventStatus === "WAITING_APPROVAL") {
                if (isStaff || userPermissions.emissionEventApprove) {
                    if (site === null) {
                        actions.push({
                            label: "Save and mark as 'Rejected'",
                            action: (e) => {
                                form.setValue(
                                    "eventStatus",
                                    EventStatusEnum.Rejected,
                                );
                                submit(e);
                            },
                            disabled: form.formState.isSubmitting,
                        });
                    } else {
                        actions.push({
                            label: "Approve and mark as 'Complete'",
                            action: (e) => {
                                form.setValue(
                                    "eventStatus",
                                    EventStatusEnum.Completed,
                                );
                                submit(e);
                            },
                            disabled: form.formState.isSubmitting,
                        });
                    }
                    actions.push({
                        label: "Request revision and send it back to 'In Progress'",
                        action: (e) => {
                            form.setValue(
                                "eventStatus",
                                EventStatusEnum.WorkInProgress,
                            );
                            submit(e);
                        },
                        disabled: form.formState.isSubmitting,
                    });
                    actions.push({
                        label: "Save and continue later",
                        action: () => {
                            form.setValue(
                                "eventStatus",
                                emission.mainEvent.eventStatus,
                            );
                            submit();
                        },
                        disabled: form.formState.isSubmitting,
                    });
                }
            }
        } else {
            if (
                emission.mainEvent.eventStatus === "NEW" ||
                emission.mainEvent.eventStatus === "CREATED" ||
                emission.mainEvent.eventStatus === "WORK_IN_PROGRESS"
            ) {
                if (site === null) {
                    actions.push({
                        label: "Save and mark as 'Rejected'",
                        action: (e) => {
                            form.setValue(
                                "eventStatus",
                                EventStatusEnum.Rejected,
                            );
                            submit(e);
                        },
                        disabled: form.formState.isSubmitting,
                    });
                } else {
                    if (
                        emission.mainEvent.eventStatus === "NEW" ||
                        emission.mainEvent.eventStatus === "CREATED"
                    ) {
                        actions.push({
                            label: "Accept and start investigation",
                            action: (e) => {
                                form.setValue(
                                    "eventStatus",
                                    EventStatusEnum.WorkInProgress,
                                );
                                submit(e);
                            },
                            disabled: form.formState.isSubmitting,
                        });
                    }
                    actions.push({
                        label: "Save and mark as 'Complete'",
                        action: (e) => {
                            form.setValue(
                                "eventStatus",
                                EventStatusEnum.Completed,
                            );
                            submit(e);
                        },
                        disabled: form.formState.isSubmitting,
                    });
                }
                if (
                    (emission.mainEvent.eventStatus === "NEW" ||
                        emission.mainEvent.eventStatus === "CREATED") &&
                    userPermissions.emissionEventArchive
                ) {
                    actions.push({
                        label: "Archive (no investigation)",
                        action: () => {
                            setShowArchiveConfirm(true);
                        },
                        disabled: form.formState.isSubmitting,
                    });
                }
                if (emission.mainEvent.eventStatus === "WORK_IN_PROGRESS") {
                    actions.push({
                        label: "Save and continue later",
                        action: () => {
                            form.setValue(
                                "eventStatus",
                                emission.mainEvent.eventStatus,
                            );
                            submit();
                        },
                        disabled: form.formState.isSubmitting,
                    });
                    if (userPermissions.emissionEventArchive) {
                        actions.push({
                            label: "Archive",
                            action: () => {
                                form.setValue(
                                    "eventStatus",
                                    EventStatusEnum.Archived,
                                );
                                submit();
                            },
                            disabled: form.formState.isSubmitting,
                        });
                    }
                }
            }
        }
    }

    return (
        <>
            <div className="flex gap-2 h-8">
                <div className="flex items-center gap-2">
                    {actions.length > 0 && (
                        <Menu as="div" className="relative">
                            <Menu.Button
                                disabled={form.formState.isSubmitting}
                                className="bg-ae-blue-500 hover:bg-ae-blue-600 disabled:opacity-70 disabled:cursor-not-allowed pl-4 pr-2 py-1.5 rounded flex items-center gap-2 text-white text-sm font-medium"
                            >
                                {form.formState.isSubmitting
                                    ? "Saving..."
                                    : "Save event"}
                                {form.formState.isSubmitting ? (
                                    <FontAwesomeIcon
                                        icon={faSpinnerScale}
                                        className="animate-spin"
                                    />
                                ) : (
                                    <FontAwesomeIcon icon={faChevronDown} />
                                )}
                            </Menu.Button>
                            <Menu.Items className="absolute top-full right-0 mt-1 bg-white rounded shadow-lg border border-ae-gray-250 z-10 min-w-[230px] overflow-hidden">
                                {actions.map((action, index) => (
                                    <Menu.Item key={index}>
                                        {({ active }) => (
                                            <button
                                                type="button"
                                                onClick={action.action}
                                                disabled={action.disabled}
                                                className={`
                                                w-full px-4 py-2 text-left text-sm
                                                ${action.disabled ? "text-gray-400 cursor-not-allowed" : "text-ae-blue-900"}
                                                ${active && !action.disabled ? "bg-ae-gray-200" : ""}
                                            `}
                                            >
                                                {action.label}
                                            </button>
                                        )}
                                    </Menu.Item>
                                ))}
                            </Menu.Items>
                        </Menu>
                    )}
                </div>
            </div>
            <ConfirmationDialog
                visible={showLinkConfirm}
                onClose={() => {
                    setShowLinkConfirm(false);
                    setPendingLinkEvent(null);
                }}
                onConfirm={async () => {
                    setShowLinkConfirm(false);
                    form.setValue("eventStatus", EventStatusEnum.Linked);
                    await submit(pendingLinkEvent);
                    setPendingLinkEvent(null);
                    onClose();
                }}
                title="Confirm linking emission?"
                message={
                    <>
                        {currentIsWip && (
                            <span>
                                Linking this emission will discard any data
                                entered on this event.
                            </span>
                        )}
                        {currentIsWip && targetIsCompleted && (
                            <br className="mb-2" />
                        )}
                        {targetIsCompleted && (
                            <span>
                                The event selected to link to is marked as{" "}
                                <b>completed</b>, linking to it will move it
                                back to <b>in progress</b> for re-review.
                            </span>
                        )}
                    </>
                }
            />
            <ConfirmationDialog
                visible={showArchiveConfirm}
                onClose={() => setShowArchiveConfirm(false)}
                onConfirm={async () => {
                    await archiveEmission();
                    setShowArchiveConfirm(false);
                    onClose();
                }}
                title="Archive emission"
                message="Are you sure you want to archive this emission? Any data entered into the form will not be saved."
            />
        </>
    );
};
